import { toast } from "react-toastify";
import { allMetrics } from "../../components/Reports/Components/utils";
import { requestBackendReportExport } from "./backendReportExport";

export const SEARCH_TERM_INSIGHTS_REPORT_TYPE = "SEARCH_TERM_INSIGHTS";
const CUBE_TABLE = "AmazonSearchtermTable";

const CUBE_MEASURE_TO_API = {
  cost: "spends",
  costDRR: "spends_drr",
  impressions: "impressions",
  impressionsDRR: "impressions_drr",
  clicks: "clicks",
  clicksDRR: "clicks_drr",
  ctr: "ctr",
  ctrDRR: "ctr_drr",
  cpc: "cpc",
  cpcDRR: "cpc_drr",
  cpm: "cpm",
  cpmDRR: "cpm_drr",
  conversions14d: "orders",
  conversions14dDRR: "orders_drr",
  conversion_rate: "cvr",
  conversion_rateDRR: "cvr_drr",
  sales14d: "revenue",
  sales14dDRR: "revenue_drr",
  average_order_value: "aov",
  average_order_valueDRR: "aov_drr",
  acos: "acos",
  acosDRR: "acos_drr",
  roas: "roas",
  roasDRR: "roas_drr",
};

const CUBE_DIMENSION_TO_API = {
  queryDimension: "search_term",
  adGroupIdDimension: "ad_group_id",
  campaignIdDimension: "campaign_id",
  portfolioIdDimension: "portfolio_id",
  adGroupNameDimension: "ad_group_name",
  campaignNameDimension: "campaign_name",
  keywordTypeDimension: "keyword_type",
  adTargetingTypeDimension: "match_type",
  campaignTypeDimension: "campaign_type",
  productSetDimension: "product_set",
  targetingTypeDimension: "targeting_type",
  portfolioNameDimension: "portfolio_name",
  weekDimension: "week",
  monthDimension: "month",
  date: "date",
};

const EXCLUDED_FILTER_MEMBERS = new Set(["profileId", "profile_id"]);

function stripTablePrefix(value = "") {
  return value.replace(`${CUBE_TABLE}.`, "");
}

function cubeMeasureToApi(measureKey) {
  return CUBE_MEASURE_TO_API[measureKey] ?? measureKey;
}

function cubeDimensionToApi(dimensionKey) {
  return CUBE_DIMENSION_TO_API[dimensionKey] ?? dimensionKey;
}

function cubeFieldToApi(fieldKey) {
  if (CUBE_MEASURE_TO_API[fieldKey]) {
    return cubeMeasureToApi(fieldKey);
  }
  return cubeDimensionToApi(fieldKey);
}

function cubeFilterToRuleItem(filter) {
  const memberKey = stripTablePrefix(filter.member);
  const isMetric = allMetrics.includes(memberKey);

  return {
    type: "RULE_ITEM",
    member: isMetric ? cubeMeasureToApi(memberKey) : cubeDimensionToApi(memberKey),
    operator: filter.operator,
    values: filter.values,
  };
}

function buildFiltersFromCubeFilters(cubeFilters = []) {
  const dimensionRules = [];
  const metricRules = [];

  cubeFilters.forEach((filter) => {
    const memberKey = stripTablePrefix(filter.member);
    if (EXCLUDED_FILTER_MEMBERS.has(memberKey)) return;

    const ruleItem = cubeFilterToRuleItem(filter);
    if (allMetrics.includes(memberKey)) {
      metricRules.push(ruleItem);
    } else {
      dimensionRules.push(ruleItem);
    }
  });

  const filters = {};
  if (dimensionRules.length > 0) {
    filters.dimensions = { type: "AND_RULE", and: dimensionRules };
  }
  if (metricRules.length > 0) {
    filters.metrics = { type: "AND_RULE", and: metricRules };
  }

  return filters;
}

export function getSearchTermInsightsVisibleColumnKeys({
  cubeQueryMeasures = [],
  cubeQueryDimensions = [],
}) {
  const dimensionKeys = cubeQueryDimensions.map((dimension) =>
    cubeDimensionToApi(stripTablePrefix(dimension))
  );
  const measureKeys = cubeQueryMeasures.map((measure) =>
    cubeMeasureToApi(stripTablePrefix(measure))
  );

  return [...dimensionKeys, ...measureKeys].filter(Boolean);
}

export function buildSearchTermInsightsExportQueryBody({
  cubeQueryMeasures = [],
  cubeQueryDimensions = [],
  cubeQueryOrder = [],
  cubeQueryFilters = [],
  cubeQueryTimeDimensions = [],
}) {
  const measures = cubeQueryMeasures.map((measure) =>
    cubeMeasureToApi(stripTablePrefix(measure))
  );
  const dimensions = cubeQueryDimensions.map((dimension) =>
    cubeDimensionToApi(stripTablePrefix(dimension))
  );
  const order = cubeQueryOrder.map(([field, direction]) => [
    cubeFieldToApi(stripTablePrefix(field)),
    direction,
  ]);
  const timeDimensions = cubeQueryTimeDimensions.map((timeDimension) => ({
    dimension: cubeDimensionToApi(stripTablePrefix(timeDimension.dimension)),
    dateRange:
      timeDimension.dateRange ?? timeDimension.compareDateRange?.[0] ?? [],
  }));

  const filters = buildFiltersFromCubeFilters(cubeQueryFilters);
  const query = {
    measures,
    dimensions,
    order,
    timeDimensions,
  };

  if (Object.keys(filters).length > 0) {
    query.filters = filters;
  }

  return { query };
}

export async function exportSearchTermInsightsReport({
  clientId,
  userPlatform = "amazon",
  triggeredBy,
  totalCount,
  queryBody,
  visibleColumnKeys,
  setIsExporting = () => {},
  onExportSuccess = () => {},
}) {
  try {
    setIsExporting(true);

    await requestBackendReportExport({
      clientId,
      userPlatform,
      reportType: SEARCH_TERM_INSIGHTS_REPORT_TYPE,
      totalCount,
      triggeredBy,
      queryBody,
      frontendConfig: {
        columns: visibleColumnKeys,
      },
    });

    onExportSuccess();
  } catch (error) {
    console.error(error);
    toast.error(
      error?.response?.data?.message ?? "Failed to submit export request."
    );
  } finally {
    setIsExporting(false);
  }
}
